import { type ButtonHTMLAttributes, type Ref, useId, useRef, useState } from "react";

import { tv, type VariantProps } from "tailwind-variants";

import { cn, type VariantSlotsToClassNames } from "@virtual-brain-lib/frontend-design-system/utils";

import { Icon, type Icons } from "./Icon";
import { Span, type SpanProps } from "./Span";
import { Tooltip, type TooltipProps } from "./Tooltip";

export const tagVariants = tv({
	slots: {
		base: "inline-flex w-fit shrink-0 cursor-default items-center justify-center overflow-hidden border border-transparent transition-all duration-200 ease-in-out",
		text: "w-full overflow-hidden font-bold text-ellipsis whitespace-nowrap",
	},
	variants: {
		size: {
			xs: {
				base: "gap-0.5 rounded-sm px-1.5 py-0.5",
			},
			sm: {
				base: "min-h-5 gap-1.5 rounded-sm px-2 py-1",
			},
			md: {
				base: "min-h-6 gap-2 rounded-md px-2.5 py-1",
			},
			lg: {
				base: "min-h-7 gap-2 rounded-lg px-3 py-1.5",
			},
			xl: {
				base: "min-h-8 gap-2 rounded-xl px-4 py-1.5",
			},
		},
		color: {
			primary: {
				base: "outline-primary-active-ring",
			},
			secondary: {
				base: "outline-secondary-active-ring",
			},
			danger: {
				base: "outline-danger-active-ring",
			},
			info: {
				base: "outline-info-active-ring",
			},
			success: {
				base: "outline-success-active-ring",
			},
			warning: {
				base: "outline-warning-active-ring",
			},
			orange: {},
			purple: {},
			yellow: {},
			gray: {},
		},
		variant: {
			solid: {
				base: "",
			},
			outline: {
				base: "bg-transparent",
			},
			ghost: {
				base: "bg-transparent",
			},
		},
		shape: {
			rectangle: {
				base: "rounded-sm",
			},
			pill: {
				base: "rounded-full",
			},
		},
	},
	compoundVariants: [
		{
			color: "primary",
			variant: "solid",
			className: {
				base: "bg-primary text-on-primary",
				text: "text-on-primary",
			},
		},
		{
			color: "primary",
			variant: "outline",
			className: {
				base: "border-primary-border text-on-primary-2",
				text: "text-on-primary-2",
			},
		},
		{
			color: "secondary",
			variant: "solid",
			className: {
				base: "bg-secondary text-on-secondary",
				text: "text-on-secondary",
			},
		},
		{
			color: "secondary",
			variant: "outline",
			className: {
				base: "border-secondary-border text-on-secondary-2",
				text: "text-on-secondary-2",
			},
		},
		{
			color: "danger",
			variant: "solid",
			className: {
				base: "bg-danger text-on-danger",
				text: "text-on-danger",
			},
		},
		{
			color: "danger",
			variant: "outline",
			className: {
				base: "border-danger-border text-on-danger-2",
				text: "text-on-danger-2",
			},
		},
		{
			color: "info",
			variant: "solid",
			className: {
				base: "bg-info text-on-info",
				text: "text-on-info",
			},
		},
		{
			color: "info",
			variant: "outline",
			className: {
				base: "border-info-border text-on-info-2",
				text: "text-on-info-2",
			},
		},
		{
			color: "success",
			variant: "solid",
			className: {
				base: "bg-success text-on-success",
				text: "text-on-success",
			},
		},
		{
			color: "success",
			variant: "outline",
			className: {
				base: "border-success-border text-on-success-2",
				text: "text-on-success-2",
			},
		},
		{
			color: "warning",
			variant: "solid",
			className: {
				base: "bg-warning text-on-warning",
				text: "text-on-warning",
			},
		},
		{
			color: "warning",
			variant: "outline",
			className: {
				base: "border-warning-border text-on-warning-2",
				text: "text-on-warning-2",
			},
		},
		{
			color: "orange",
			variant: "solid",
			className: {
				base: "bg-tag-orange text-on-tag-orange",
				text: "text-on-tag-orange",
			},
		},
		{
			color: "purple",
			variant: "solid",
			className: {
				base: "bg-tag-purple text-on-tag-purple",
				text: "text-on-tag-purple",
			},
		},
		{
			color: "gray",
			variant: "ghost",
			className: {
				text: "text-zinc-500",
			},
		},
	],
});

export type TagVariant = VariantProps<typeof tagVariants>;

export type TagProps = TagVariant &
	ButtonHTMLAttributes<HTMLButtonElement> & {
		readonly leftIcon?: Icons;
		readonly rightIcon?: Icons;
		readonly ref?: Ref<HTMLButtonElement>;
		readonly tooltip?: Omit<TooltipProps, "anchor">;
		readonly classNames?: VariantSlotsToClassNames<typeof tagVariants>;
	};

const textVariant: Record<NonNullable<TagProps["size"]>, NonNullable<SpanProps["variant"]>> = {
	xs: "tag-xs",
	sm: "tag-sm",
	md: "tag-md",
	lg: "tag-lg",
	xl: "tag-xl",
};

export function Tag({
	leftIcon,
	rightIcon,
	children,
	size = "md",
	tooltip: tooltipProps,
	className,
	color = "primary",
	variant = "solid",
	shape = "rectangle",
	classNames,
	...props
}: TagProps) {
	const { base, text } = tagVariants({ color, variant, shape, size, ...props });
	const tooltipId = useId();
	const textRef = useRef<HTMLSpanElement>(null);

	const [isTextTruncated, setIsTextTruncated] = useState(false);

	const checkTruncation = () => {
		if (textRef.current) {
			const isTruncated = textRef.current.scrollWidth > textRef.current.clientWidth;

			setIsTextTruncated(isTruncated);
		}
	};

	const tooltip =
		isTextTruncated && (!tooltipProps || tooltipProps?.hidden) ? { children } : tooltipProps;

	return (
		<>
			<Span
				{...props}
				className={base({ className: cn(className, classNames?.base) })}
				data-tooltip={tooltip ? tooltipId : undefined}
				onMouseEnter={checkTruncation}
			>
				{leftIcon && <Icon name={leftIcon} size={size} />}
				{children && (
					<Span
						ref={textRef}
						className={text({ className: classNames?.text })}
						variant={textVariant[size]}
					>
						{children}
					</Span>
				)}
				{rightIcon && <Icon name={rightIcon} size={size} />}
			</Span>
			{tooltip && <Tooltip anchor={tooltipId} {...tooltip} />}
		</>
	);
}
